import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { openingBalanceService } from "services";
import {
  OpeningBalanceForm,
  OpeningBalanceQuery,
  OpeningBalanceUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type OpeningBalanceListKey =
  | ["opening-balance"]
  | ["opening-balance", OpeningBalanceQuery];
type OpeningBalanceDetailKey = ["opening-balance", "detail", number | string];

const openingBalanceKeys = {
  all: ["opening-balance"] as const,
  list: (params?: OpeningBalanceQuery): OpeningBalanceListKey =>
    params ? ["opening-balance", params] : ["opening-balance"],
  detail: (id: number | string): OpeningBalanceDetailKey => [
    "opening-balance",
    "detail",
    id,
  ],
};

export function useOpeningBalance() {
  const queryClient = useQueryClient();

  const fetchList = (params?: OpeningBalanceQuery, enabled = true) => {
    return useQuery({
      queryKey: openingBalanceKeys.list(params),
      queryFn: () => openingBalanceService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: Math.max(1, data?.totalPage ?? 1),
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: openingBalanceKeys.detail(id),
      queryFn: () => openingBalanceService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createOpeningBalance = useMutation({
    mutationFn: (payload: OpeningBalanceForm) =>
      openingBalanceService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo số dư đầu kỳ thành công");
      }
      queryClient.invalidateQueries({ queryKey: openingBalanceKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo số dư đầu kỳ thất bại");
      console.error("createOpeningBalance failed", err);
    },
  });

  const updateOpeningBalance = useMutation({
    mutationFn: (payload: OpeningBalanceUpdatePayload) =>
      openingBalanceService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật số dư đầu kỳ thành công");
      }
      queryClient.invalidateQueries({ queryKey: openingBalanceKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật số dư đầu kỳ thất bại");
      console.error("updateOpeningBalance failed", err);
    },
  });

  const deleteOpeningBalance = useMutation({
    mutationFn: (id: number | string) => openingBalanceService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá số dư đầu kỳ thành công");
      }
      queryClient.invalidateQueries({ queryKey: openingBalanceKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá số dư đầu kỳ thất bại");
      console.error("deleteOpeningBalance failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createOpeningBalance,
    updateOpeningBalance,
    deleteOpeningBalance,
  };
}
